import './style.css';
import { RingLog } from './log';
import { applyUrlOverrides, loadConfig } from './config';
import type { AppConfig, Artwork, Backend, RecognizerEvent } from './types';
import { Preprocessor } from './preprocess';
import { postprocess } from './postprocess';
import { Recognizer } from './recognizer';
import { createDetector, defaultAttempts, type Detector } from './detector';
import { CameraError, openCamera, stopCamera } from './camera';
import type { AudioPlayer, AudioState } from './audio/player';
import { WebAudioPlayer } from './audio/webaudio';
import { ElementPlayer } from './audio/element';
import { IosUnmute, isIOS } from './audio/unmute';
import { AudioPolicy } from './audio/policy';
import { Screens, type Screen } from './ui/screens';
import { renderCard } from './ui/card';
import { Hud } from './ui/hud';

export interface AppStatus {
  phase: Screen;
  backend: Backend | null;
  threads: number;
  isolated: boolean;
  error: string | null;
  videoReady: boolean;
  inferCount: number;
  lastInferMs: number;
  avgInferMs: number;
  fps: number;
  bestScore: number;
  hits: Record<string, number>;
  /** recognizer が今追従している作品(continuous では lost で null に戻る)。 */
  recognized: string | null;
  /** 音声が流れている作品。E2E の判定はこちら。 */
  playing: string | null;
  audioState: AudioState;
  audioPlaying: boolean;
  muted: boolean;
  audio: string;
}

const params = new URLSearchParams(location.search);
const DEBUG = params.get('debug') === '1';
const AUTOSTART = params.get('autostart') === '1';
const ep = params.get('ep');
const FORCED_EP: Backend | null = ep === 'webgpu' || ep === 'wasm' ? ep : null;

class App {
  private readonly screens = new Screens(document);
  private readonly hud = new Hud(document, DEBUG);
  private readonly log = new RingLog(50, () => this.hud.setLog(this.log.lines()));
  private config: AppConfig | null = null;
  private detector: Detector | null = null;
  private pre: Preprocessor | null = null;
  private recognizer: Recognizer | null = null;
  private player: AudioPlayer | null = null;
  private policy: AudioPolicy | null = null;
  private unmute: IosUnmute | null = null;
  private wakeLock: WakeLockSentinel | null = null;
  private phase: Screen = 'intro';
  private error: string | null = null;
  private running = false;
  private restoring = false;
  private openingCamera = false;
  private loopId = 0;
  private stats = { inferCount: 0, lastInferMs: 0, avgInferMs: 0, fps: 0, lastTick: 0, bestScore: 0 };

  constructor() {
    this.screens.startButton.addEventListener('click', () => void this.start());
    this.screens.retryButton.addEventListener('click', () => void this.start());
    this.screens.overlayButton.addEventListener('click', () => void this.resumeFromTap());
    this.screens.muteButton.addEventListener('click', () => this.toggleMute());
    this.hud.recognizeButton.addEventListener('click', () => void this.debugRecognize());
    document.addEventListener('visibilitychange', () => void this.onVisibility());
    void this.boot();
  }

  private async boot(): Promise<void> {
    this.screens.startButton.disabled = true; // 設定が来るまで「はじめる」は無効(押しても何も起きないのを防ぐ)
    try {
      this.config = applyUrlOverrides(await loadConfig('/artworks.json'), params);
      this.screens.setIntro(this.config.artworks[0]!);
      this.log.push(`config ok: ${this.config.artworks.length} artwork(s), model=${this.config.model.url}`);
    } catch (e) {
      this.fail(`設定を読み込めませんでした: ${(e as Error).message}`);
    } finally {
      this.screens.startButton.disabled = false;
    }
    if (AUTOSTART && this.config) void this.start();
  }

  /** Start / Retry のタップから呼ぶ。音声の unlock はここで同期的に行う。 */
  async start(): Promise<void> {
    if (this.running) return;
    if (!this.config) {
      // 設定の取得に失敗したあとの「もう一度」: まず設定を取り直す(この経路だけ unlock より前に await が入る)
      await this.boot();
      if (!this.config) return; // boot() が fail() 済み
    }
    if (this.phase === 'loading' || this.running) return; // AUTOSTART の boot() が既に start() を始めていた場合も含む
    const cfg = this.config;
    this.error = null;
    this.screens.setOverlay(false);
    stopCamera(this.screens.video); // 前回の失敗/中断で開いたままのストリームを確実に閉じてから開き直す
    if (!this.player) {
      this.player = cfg.audio.backend === 'element' ? new ElementPlayer((m) => this.log.push(m)) : new WebAudioPlayer(cfg.audio.fadeMs, (m) => this.log.push(m));
      this.screens.setMuted(this.player.muted);
      this.policy = new AudioPolicy(this.player, cfg.artworks, cfg.audio.policy, (m) => this.log.push(m));
      if (cfg.audio.iosUnmute && isIOS(navigator.userAgent, navigator.maxTouchPoints)) this.unmute = new IosUnmute();
    }
    this.player.unlock(); // 同期: タップの効力が残っているうちに
    this.unmute?.kick();
    if (DEBUG && this.player instanceof WebAudioPlayer) this.player.enableProbe(); // unlock 後(AudioContext 生成後)
    this.setPhase('loading');
    try {
      this.screens.setProgress('音声を準備しています…', null);
      await Promise.all(cfg.artworks.map((a) => this.player!.preload(a)));
      this.screens.setProgress('カメラを開いています…', null);
      const { width, height } = await this.openCameraSafely();
      this.log.push(`camera ${width}x${height}`);
      if (!this.detector) {
        this.detector = await this.buildDetector(FORCED_EP ? [FORCED_EP] : defaultAttempts());
        this.pre = new Preprocessor(this.detector.inputSize);
      }
      this.recognizer = new Recognizer(cfg.detection);
      this.setPhase('scanning');
      // ここから先(WakeLock・音声の resume・ループ開始)は復帰処理と同じ。読み込み中に背面へ回っていた場合も restore() が面倒を見る。
      await this.restore();
    } catch (e) {
      if (e instanceof CameraError && e.kind !== 'error') {
        this.setPhase('unsupported');
        this.screens.setUnsupported(e.kind, location.href);
        this.error = e.message;
      } else {
        this.fail(`開始できませんでした: ${(e as Error).message}`);
      }
    }
  }

  private buildDetector(attempts: Backend[]): Promise<Detector> {
    return createDetector(this.config!.model, (p) => {
      if (p.stage === 'download') {
        const frac = p.total ? p.loaded! / p.total : null;
        this.screens.setProgress(`モデルを取得しています… ${frac !== null ? Math.round(frac * 100) + '%' : ''}`, frac);
      } else {
        this.screens.setProgress(p.stage === 'init' ? `推論エンジンを準備しています (${p.backend})…` : 'ウォームアップ中…', null);
      }
    }, (m) => this.log.push(m), attempts);
  }

  private async loop(id: number): Promise<void> {
    const cfg = this.config!;
    const det = this.detector!;
    const pre = this.pre!;
    const video = this.screens.video;
    let warnedNotReady = false;
    while (this.running && this.loopId === id) {
      const t0 = performance.now();
      // 復帰直後などフレームがまだ来ていないとき: letterbox が 0 除算になるので 1 周見送る。
      if (video.videoWidth <= 0 || video.videoHeight <= 0) {
        if (!warnedNotReady) { this.log.push('video not ready'); warnedNotReady = true; }
        await new Promise((r) => setTimeout(r, cfg.detection.intervalMs));
        continue;
      }
      warnedNotReady = false;
      try {
        const { data, geom } = pre.toTensor(video, video.videoWidth, video.videoHeight);
        const t1 = performance.now();
        const out = await det.run(data);
        const t2 = performance.now();
        const dets = postprocess(out.data, out.dims, geom, cfg.detection);
        this.stats.inferCount += 1;
        this.stats.lastInferMs = t2 - t1;
        this.stats.avgInferMs = this.stats.avgInferMs ? this.stats.avgInferMs * 0.9 + this.stats.lastInferMs * 0.1 : this.stats.lastInferMs;
        if (this.stats.lastTick) this.stats.fps = 1000 / (t0 - this.stats.lastTick);
        this.stats.lastTick = t0;
        this.stats.bestScore = dets[0]?.score ?? 0;
        // 推論 try の外側扱い: UI 側の例外(カード描画・HUD・再生方針)で WebGPU→WASM 切替を誤爆させない。
        try {
          for (const ev of this.recognizer!.update(dets, t0)) await this.handleEvent(ev);
          this.hud.drawBoxes(dets, (d) => d.score >= cfg.detection.enter);
          this.renderHud();
        } catch (e) {
          this.log.push(`ui error: ${(e as Error).message}`);
        }
      } catch (e) {
        // 仕様 7 節: 推論中の例外は次の候補(WebGPU → WASM)で作り直して継続。WASM でも駄目なら error。
        this.log.push(`inference error on ${det.backend}: ${(e as Error).message}`);
        this.detector = null;
        await det.release().catch(() => {});
        if (det.backend === 'webgpu') {
          const prev = this.phase;
          this.setPhase('loading');
          try {
            this.detector = await this.buildDetector(['wasm']);
            this.pre = new Preprocessor(this.detector.inputSize);
            this.setPhase(prev);
            this.log.push('switched to wasm');
            await this.restore(); // 作り直しの間に背面へ回っていた場合も含めて、カメラ・音声・ループを戻す
          } catch (e2) {
            this.fail(`推論でエラーが起きました: ${(e2 as Error).message}`);
          }
          return;
        }
        this.fail(`推論でエラーが起きました: ${(e as Error).message}`);
        return;
      }
      const wait = Math.max(0, cfg.detection.intervalMs - (performance.now() - t0));
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  /** 認識イベントの処理。推論ループとデバッグ用「認識テスト」ボタンで同一経路を通す。 */
  private async handleEvent(ev: RecognizerEvent): Promise<void> {
    const cfg = this.config!;
    if (ev.type === 'recognized') {
      const a = cfg.artworks.find((x) => x.id === ev.cls);
      if (a) { renderCard(this.screens.cardElement, a); this.setPhase('playing'); }
      this.log.push(`recognized cls=${ev.cls} score=${ev.box.score.toFixed(2)}`);
    } else if (ev.type === 'lost') {
      this.log.push(`lost cls=${ev.cls}`);
      if (cfg.audio.policy === 'follow') this.setPhase('scanning');
    }
    await this.policy!.handle(ev).catch((err) => {
      this.log.push(`audio play failed: ${(err as Error).message}`);
      this.screens.setOverlay(true);
    });
  }

  /** デバッグ用: 検出なしで「認識できた」判定を発火させる。本物と同じ handleEvent を通す。 */
  private async debugRecognize(): Promise<void> {
    if (!this.recognizer || !this.running || !this.config) { this.log.push('debug recognize: not running'); return; }
    if (this.recognizer.current !== null) { this.log.push('debug recognize: already recognized'); return; }
    const id = this.config.artworks[0]!.id;
    this.log.push(`debug recognize: force cls=${id}`);
    for (const ev of this.recognizer.force(id, performance.now())) await this.handleEvent(ev);
  }

  /**
   * カメラを開く。開いている最中は hidden 側の stopCamera を抑止する:
   * 保留中の video.play() を srcObject=null で中断させると openCamera が AbortError で失敗し、
   * 「開始できませんでした」の error 画面に落ちてしまうため。開き終えた時点でまだ隠れていればここで閉じる。
   */
  private async openCameraSafely(): Promise<{ width: number; height: number }> {
    this.openingCamera = true;
    try {
      const size = await openCamera(this.screens.video);
      if (document.visibilityState === 'hidden') stopCamera(this.screens.video);
      return size;
    } finally {
      this.openingCamera = false;
    }
  }

  private async onVisibility(): Promise<void> {
    if (document.visibilityState === 'hidden') {
      // 復帰処理が openCamera/resume を await している最中に再び隠れた場合も確実に止める(冪等)。
      this.running = false;
      await this.player?.pause();
      if (!this.openingCamera) stopCamera(this.screens.video); // 開いている最中なら openCameraSafely 側が閉じる
      this.log.push('hidden: paused');
      return;
    }
    if (this.phase !== 'scanning' && this.phase !== 'playing') return;
    if (this.restoring) return;
    this.restoring = true;
    try {
      await this.restore();
    } catch (e) {
      this.fail(`再開できませんでした: ${(e as Error).message}`);
    } finally {
      this.restoring = false;
    }
  }

  /**
   * カメラ・WakeLock・音声・推論ループをまとめて動く状態に戻す。start() の最後、可視化での復帰、
   * WASM への作り直しのあとの共通経路。await のたびに再び隠れていないか確認し、隠れていたら後片付けして抜ける。
   */
  private async restore(): Promise<void> {
    if (await this.abortRestoreIfHidden()) return;
    const video = this.screens.video;
    const stream = video.srcObject as MediaStream | null;
    const live = !!stream && stream.getTracks().some((t) => t.readyState === 'live');
    if (!live) {
      stopCamera(video); // 終了済みトラックが残っていれば捨ててから開き直す
      const { width, height } = await this.openCameraSafely(); // iOS は背面でストリームを止めるので取り直す
      this.log.push(`camera ${width}x${height}`);
      if (await this.abortRestoreIfHidden()) return;
    }
    await this.requestWakeLock();
    if (await this.abortRestoreIfHidden()) return;
    const ok = this.player ? await this.player.resume() : true;
    if (await this.abortRestoreIfHidden()) return;
    this.screens.setOverlay(!ok);
    this.log.push(`restore: camera ${live ? 'kept' : 'reopened'}, audio ${ok ? 'resumed' : 'needs tap'}`);
    this.running = true;
    void this.loop(++this.loopId);
  }

  /** 復帰処理の各 await の直後に呼ぶ: その間に再び隠れていたら復帰を中断する。 */
  private async abortRestoreIfHidden(): Promise<boolean> {
    if (document.visibilityState !== 'hidden') return false;
    this.running = false;
    await this.player?.pause();
    stopCamera(this.screens.video);
    return true;
  }

  /** 「タップして再開」: 操作ハンドラ内で unlock → resume → 必要なら再生し直す。 */
  private async resumeFromTap(): Promise<void> {
    const player = this.player;
    if (!player) return;
    player.unlock();
    this.unmute?.kick();
    const ok = await player.resume();
    if (ok) {
      // 初回再生が失敗してオーバーレイを出した場合、player.current は null のまま(両実装とも代入前に throw する)。
      // その場合は recognizer が今追従している作品で鳴らし直す。
      const cls = this.recognizer?.current;
      const target = player.current ?? (cls !== null && cls !== undefined ? this.config?.artworks.find((a) => a.id === cls) ?? null : null);
      if (target) await player.play(target).catch((e) => this.log.push(`resume play failed: ${(e as Error).message}`));
    }
    this.screens.setOverlay(!ok);
    this.log.push(`resume from tap: ${ok}`);
  }

  /** 消音の切り替え。再生は止めず無音にするだけなので、認識や方針には影響しない。 */
  private toggleMute(): void {
    if (!this.player) return;
    this.player.setMuted(!this.player.muted);
    this.screens.setMuted(this.player.muted);
    this.log.push(`mute ${this.player.muted ? 'on' : 'off'}`);
  }

  private async requestWakeLock(): Promise<void> {
    try {
      this.wakeLock = (await navigator.wakeLock?.request('screen')) ?? null;
    } catch {
      this.wakeLock = null;
    }
  }

  private setPhase(p: Screen): void {
    this.phase = p;
    this.screens.show(p);
    this.renderHud();
  }

  private fail(message: string): void {
    this.error = message;
    this.running = false;
    stopCamera(this.screens.video);
    this.screens.setOverlay(false);
    this.log.push(`ERROR ${message}`);
    this.setPhase('error');
    this.screens.setError(message);
  }

  private renderHud(): void {
    if (!DEBUG) return;
    const s = this.stats;
    this.hud.update({
      ep: `${this.detector?.backend ?? '-'} thr=${this.detector?.threads ?? '-'}${self.crossOriginIsolated ? ' iso' : ''}`,
      infer: `#${s.inferCount} ${s.lastInferMs.toFixed(0)}ms avg ${s.avgInferMs.toFixed(0)} ${s.fps.toFixed(1)}fps`,
      best: s.bestScore.toFixed(2),
      hits: [...(this.recognizer?.hits ?? [])].map(([k, v]) => `${k}:${v}`).join(' ') || '-',
      audio: this.player?.diagnostics() ?? '-',
    });
  }

  get status(): AppStatus {
    const s = this.stats;
    const v = this.screens.video;
    const artwork: Artwork | undefined = this.config?.artworks.find((a) => a.id === this.recognizer?.current);
    return {
      phase: this.phase,
      backend: this.detector?.backend ?? null,
      threads: this.detector?.threads ?? 0,
      isolated: !!self.crossOriginIsolated,
      error: this.error,
      videoReady: v.videoWidth > 0 && !v.paused,
      inferCount: s.inferCount,
      lastInferMs: Math.round(s.lastInferMs),
      avgInferMs: Math.round(s.avgInferMs),
      fps: +s.fps.toFixed(1),
      bestScore: +s.bestScore.toFixed(3),
      hits: Object.fromEntries([...(this.recognizer?.hits ?? [])].map(([k, v]) => [String(k), v])),
      recognized: artwork?.title ?? null,
      playing: this.player?.current?.title ?? null,
      audioState: this.player?.state ?? 'idle',
      audioPlaying: this.player?.state === 'playing',
      muted: this.player?.muted ?? false,
      audio: this.player?.diagnostics() ?? '',
    };
  }
}

const app = new App();
declare global {
  interface Window { __app: { start(): Promise<void>; readonly status: AppStatus } }
}
window.__app = { start: () => app.start(), get status() { return app.status; } };
